import React, { useState, useEffect, useMemo } from 'react';
import { View, Text } from 'react-native';
import { connect } from 'react-redux';
import i18n from 'i18n-js';

import { scale, role } from '@components/frame';
import { setSecureValue, navigateBack } from '@helpers';
import { filterSearchResultsForOperator } from '@config/operatorRestrictions';
import { font } from '@styles/fonts';
import Image from '@components/common/Image';
import TouchableElement from '@components/common/touchableElement';
import Loading from '@components/loading';

import Preview from '@components/imageHandling/components/thumbnails';
import NoResult from '@components/search/components/noResult';
import { useHandleMainDetailsScreenNavigation } from '@v2/pages/common/hooks/useHandleMainDetailsScreenNavigation';
import { PartDetailsScreenName } from '@v2/pages/part/details/navigation/config';
import { PartDetailsScreen } from '@v2/pages/part/details/common/enums/partDetails.enum';
import { Perspective } from '@v2/pages/common/enums';
import { ToolsDetailsScreenName } from '@v2/pages/tools/details/navigation/config';
import { ToolsDetailsScreen } from '@v2/pages/tools/details/common/enums/toolsDetails.enum';
import { NCFileDetailsScreenName } from '@v2/pages/nc-file/details/navigation/config';
import { NCFileDetailsScreen } from '@v2/pages/nc-file/details/common/enums/ncFileDetails.enum';
import { BatchDetailsScreenName } from '@v2/pages/batch/details/navigation/config';
import { BatchDetailsScreen } from '@v2/pages/batch/details/common/enums/batchDetails.enum';
import { SCREEN_PADDING_HORIZONTAL, TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { updateImageMetadata } from '@reducers/imageHandling';
import { useHandleImageNavigation } from '@v2/pages/common/hooks/useHandleImageNavigation';
import { JobDetailsScreenName } from '@v2/pages/job/navigation/config';
import { JobDetailsScreen } from '@v2/pages/job/common/enums/jobDetails.enum';
import { ProductionOrderDetailsScreenName } from '@v2/pages/productionOrder/navigation/config';
import { ProductionOrderDetailsScreen } from '@v2/pages/productionOrder/common/enums/productionOrderDetails.enum';
import { trackEvent, TRACKING_EVENTS, itemTypeToSearchItemType } from '@services/tracking';
import { getScopeIdForItemType, TEXT_MAPPING } from './helpers';

export const SearchItem = ({
  C,
  item,
  generateImageURL,
  setRecentlyViewed,
  recentlyViewed = [],
  setSelectedNCFile,
  attachMode,
  attachImageId,
  updateImageMetadata,
  onItemSelect,
  isPopupMode,
}) => {
  const { navigateToMainDetailsScreen } = useHandleMainDetailsScreenNavigation();
  const { handleImageNavigation } = useHandleImageNavigation();

  const [imageLink, setImageLink] = useState(null);

  // Function to attach item to image
  const attachItemToImage = (itemType, itemId, itemName) => {
    if (!attachImageId) return;

    const scopeId = getScopeIdForItemType(itemType);
    if (scopeId == null) {
      console.warn('Unknown item type for attachment:', itemType);
      return;
    }

    const backendData = {
      documentId: attachImageId,
      documentLinks: [
        {
          scopeId,
          scopeReferenceId: itemId,
          scopeReferenceName: itemName,
          isFavorite: false,
          isActivated: true,
          isFavoriteChanged: false,
        },
      ],
      documentTags: [],
    };

    updateImageMetadata(backendData);
    navigateBack();
  };
  const { documentData, itemType, itemDescription, itemMemo, itemId } = item;
  useEffect(() => {
    (async () => {
      if (documentData?.contentHash) {
        const imageLink = await generateImageURL({
          size: '_1x',
          cropped: documentData?.isCropped,
          item: { imageHash: documentData?.contentHash, extensionDerivative: documentData?.extensionDerivative },
          page: documentData?.pageNumber,
        });
        setImageLink(imageLink);
      }
    })();
  }, []);

  const onPress = () => {
    // If in popup mode, use onItemSelect callback
    if (isPopupMode && onItemSelect) {
      onItemSelect(item, itemType);
      return;
    }

    // If in attachment mode, attach directly
    if (attachMode && attachImageId) {
      const itemName = item.name || item.batchNumber || itemDescription || 'Unknown';
      attachItemToImage(itemType, itemId, itemName);
      return;
    }

    trackEvent(TRACKING_EVENTS.SEARCH, {
      action: 'resultTapped',
      resultItemType: itemTypeToSearchItemType(itemType),
    });

    // Otherwise, handle navigation and recently viewed as usual
    if (setRecentlyViewed) {
      const exist = recentlyViewed.find((i) => i.itemId === item.itemId && i.itemType === item.itemType);
      if (!exist) {
        const d = [item, ...recentlyViewed];
        setRecentlyViewed(d);
        setSecureValue('recentlyViewed', JSON.stringify(d));
      }
    }

    switch (itemType) {
      case 'ProductionOperation':
        return navigateToMainDetailsScreen({
          mainScreen: JobDetailsScreenName,
          subScreen: JobDetailsScreen.JOBS,
          params: {
            perspective: Perspective.JOBS,
            productionOperationId: itemId,
          },
        });
      case 'ProductionOrder':
        return navigateToMainDetailsScreen({
          mainScreen: ProductionOrderDetailsScreenName,
          subScreen: ProductionOrderDetailsScreen.ORDER,
          params: {
            perspective: Perspective.ORDER,
            productionOrderId: itemId,
          },
        });
      case 'ProductionBatch':
        return navigateToMainDetailsScreen({
          mainScreen: BatchDetailsScreenName,
          subScreen: BatchDetailsScreen.BATCH,
          params: {
            perspective: Perspective.BATCH,
            batchId: itemId,
            batchNumber: item.batchNumber,
            ncFileStatus: item.ncFileStatus,
          },
        });
      case 'Part':
        return navigateToMainDetailsScreen({
          mainScreen: PartDetailsScreenName,
          subScreen: PartDetailsScreen.PART,
          params: {
            perspective: Perspective.PART,
            partName: item.name,
            partId: itemId,
          },
        });
      case 'NcFile':
        return navigateToMainDetailsScreen({
          mainScreen: NCFileDetailsScreenName,
          subScreen: NCFileDetailsScreen.NC_FILE,
          params: {
            perspective: Perspective.NC_FILE,
            ncFileId: itemId,
            ncFileName: item.name,
            ncFileStatus: item.ncFileStatus,
          },
        });
      case 'Tool':
        return navigateToMainDetailsScreen({
          mainScreen: ToolsDetailsScreenName,
          subScreen: ToolsDetailsScreen.TOOLS,
          params: {
            perspective: Perspective.TOOLS,
            toolId: itemId,
            toolName: item.name,
          },
        });
      case 'ImageDocument':
      case 'OtherDocument':
        return handleImageNavigation({ documentId: itemId });
    }
  };

  return (
    <>
      <TouchableElement testID={TEST_IDS.searchScreen.all.searchResultItem} onPress={onPress}>
        {({ boxStyle, pressHoverColor }) => (
          <View
            style={[
              boxStyle,
              {
                height: scale(60),
                paddingHorizontal: scale(SCREEN_PADDING_HORIZONTAL),
                backgroundColor: C(pressHoverColor('Background/Light/System')),
                justifyContent: 'center',
                borderRadius: scale(10),
              },
            ]}
          >
            <View style={{ width: '100%', flexDirection: 'row', flexDirection: 'row', alignItems: 'center', overflow: 'hidden' }}>
              <View style={{ width: scale(66), height: scale(44) }}>
                {!documentData?.isImage && documentData?.extensionOriginal ? (
                  <Preview {...{ item: { ...documentData, isDocument: true }, small: true, round: true }} />
                ) : (
                  <Image
                    {...{
                      shadowColor: 'transparent',
                      isShadow: Boolean(imageLink),
                      isOverlay: true,
                      borderRadius: scale(10),
                      resizeMode: 'contain',
                      style: {
                        width: scale(66),
                        height: scale(44),
                        resizeMode: 'contain',
                      },
                      source: { uri: imageLink },
                      alt: 'img',
                      C,
                    }}
                  />
                )}
              </View>
              <View style={{ flex: 1, paddingLeft: scale(10), height: scale(44) }}>
                <Text numberOfLines={1}>
                  <Text style={[font['Button 1'], { color: C('Label/Light/Tertiary') }]}>{i18n.t(TEXT_MAPPING[itemType])} </Text>
                  {itemType === 'ProductionBatch' && item.highlightedText ? (
                    <>
                      <Text style={[font['Button 1'], { color: C('Label/Light/Primary') }]}>{item.highlightedText} </Text>
                      <Text style={[font['Subhead 1'], { color: C('Label/Light/Tertiary') }]}>
                        {itemDescription.startsWith(item.highlightedText)
                          ? itemDescription.substring(item.highlightedText.length).trim()
                          : itemDescription}
                      </Text>
                    </>
                  ) : (
                    <>
                      <Text style={[font['Button 1'], { color: C('Label/Light/Primary') }]}>{itemDescription} </Text>
                      <Text style={[font['Subhead 1'], { color: C('Label/Light/Tertiary') }]}>{itemMemo ? `- ${itemMemo}` : ''}</Text>
                    </>
                  )}
                </Text>
              </View>
            </View>
          </View>
        )}
      </TouchableElement>
    </>
  );
};

const SearchResults = ({
  C,
  loaded,
  searchResult,
  generateImageURL,
  loadMore,
  setRecentlyViewed,
  recentlyViewed,
  isSearchTriggered,
  setSelectedNCFile,
  attachMode,
  attachImageId,
  updateImageMetadata,
  onItemSelect,
  isPopupMode,
}) => {
  // Filter search results for operator role restrictions (only show Parts, NC Files, Tools in "All" search)
  const filteredSearchResult = useMemo(() => filterSearchResultsForOperator(role, searchResult), [searchResult]);

  if (!loaded) {
    return <Loading />;
  }

  if (!filteredSearchResult.length && isSearchTriggered) {
    return <NoResult {...{ C, value: isSearchTriggered }} />;
  }

  return (
    <View testID={TEST_IDS.searchScreen.all.searchResultsContainer} style={{ flex: 1, width: '100%', alignItems: 'center' }}>
      <View style={{ flex: 1, width: '100%' }}>
        {filteredSearchResult?.map((item) => (
          <SearchItem
            {...{
              key: item.itemId,
              item,
              C,
              generateImageURL,
              setRecentlyViewed,
              recentlyViewed,
              setSelectedNCFile,
              attachMode,
              attachImageId,
              updateImageMetadata,
              onItemSelect,
              isPopupMode,
            }}
          />
        ))}
      </View>
    </View>
  );
};

export default connect(null, { updateImageMetadata })(SearchResults);
